import React, { useState, useEffect, useRef } from "react";
import { Button, Input, Space, Table, message, Modal } from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ExpandAltOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AddTeamLead from "../teamleads/addTeamLead";
import EditTeamLeadForm from "../teamleads/editTeamlead";
import ViewTeamLeadDetails from "../teamleads/viewTeamlead";
import "./teamleadsV2.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const apiUrl = process.env.REACT_APP_API_Teamleads;
const teamDeleteApiUrl = process.env.REACT_APP_API_Team;

const TeamleadsV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [teamleads, setTeamleads] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [teamleadId, setTeamleadId] = useState(null);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewModalVisible, setviewModalVisible] = useState(false);
  const [editModalVisible, setEidtModalVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  useEffect(() => {
    fetchData();
  }, [open, editModalVisible]);

  const fetchData = async () => {
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Failed to fetch team leads");
      const data = await response.json();
      setTeamleads(data.body || []);
    } catch (error) {
      console.error("Error fetching team leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="teamleads-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="teamleads-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "teamleads-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex]?.toString() || "").toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleViewClick = (record) => {
    setviewModalVisible(true);
    setTeamleadId(record.id);
  };

  const handleEditClick = (record) => {
    setEidtModalVisible(true);
    setTeamleadId(record.id);
  };

  const handleDeleteClick = (record) => {
    setSelectedRecord(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(`${teamDeleteApiUrl}/${selectedRecord.id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        messageApi.success("Team lead deleted successfully");
        setTeamleads(teamleads.filter((r) => r.id !== selectedRecord.id));
      } else {
        messageApi.error("Failed to delete team lead");
      }
    } catch (error) {
      messageApi.error("Failed to delete team lead");
    }
    setDeleteModalVisible(false);
  };

  const handleAddClick = () => setOpen(true);

  const columns = [
    {
      title: "Name",
      dataIndex: "first_name",
      key: "first_name",
      width: "20%",
      ...getColumnSearchProps("first_name"),
      sorter: (a, b) => (a.first_name || "").localeCompare(b.first_name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Team Name",
      dataIndex: "team_name",
      key: "team_name",
      width: "20%",
      ...getColumnSearchProps("team_name"),
      sorter: (a, b) => (a.team_name || "").localeCompare(b.team_name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: "25%",
      ...getColumnSearchProps("email"),
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Actions",
      key: "actions",
      width: "35%",
      render: (_, record) => (
        <div className="teamleads-v2-actions">
          <Button
            type="default"
            icon={<ExpandAltOutlined />}
            onClick={() => handleViewClick(record)}
            className="teamleads-v2-btn-view"
          >
            View
          </Button>
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
            className="teamleads-v2-btn-edit"
          >
            Edit
          </Button>
          <Button
            type="default"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClick(record)}
            danger
            className="teamleads-v2-btn-delete"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="teamleads-v2">
      {contextHolder}
      <div className="teamleads-v2-header">
        <h1 className="teamleads-v2-title">All Team Leads</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddClick}
          className="teamleads-v2-add-btn"
        >
          Add Team Lead
        </Button>
      </div>

      <div className="teamleads-v2-card">
        <Table
          columns={columns}
          dataSource={teamleads}
          loading={loading}
          rowKey="id"
          locale={{ emptyText: loading ? "Loading..." : "No team leads yet" }}
          pagination={{
            showSizeChanger: true,
            defaultPageSize: 10,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total) => `Total ${total} team leads`,
          }}
          className="teamleads-v2-table"
        />
      </div>

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        className="teamleads-v2-delete-modal"
        width={420}
      >
        <p className="teamleads-v2-delete-text">
          Are you sure you want to delete{" "}
          <strong>{selectedRecord?.first_name} {selectedRecord?.last_name}</strong>? This action cannot be undone.
        </p>
      </Modal>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="teamleads-v2-dialog"
        PaperProps={{
          className: "teamleads-v2-dialog-paper",
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <AddTeamLead setOpen={setOpen} />
      </Dialog>

      <Dialog
        open={viewModalVisible}
        onClose={() => setviewModalVisible(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="teamleads-v2-dialog"
        PaperProps={{
          className: "teamleads-v2-dialog-paper",
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <ViewTeamLeadDetails setOpen={setviewModalVisible} teamId={teamleadId} />
      </Dialog>

      <Dialog
        open={editModalVisible}
        onClose={() => setEidtModalVisible(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="teamleads-v2-dialog"
        PaperProps={{
          className: "teamleads-v2-dialog-paper",
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <EditTeamLeadForm setOpen={setEidtModalVisible} id={teamleadId} />
      </Dialog>
    </div>
  );
};

export default TeamleadsV2;
